import advancedToolsImage from '@/shared/assets/images/advanced-tools-tooltip.png';
import allLanguagesImage from '@/shared/assets/images/all-languages-tooltip.png';
import demoProjectWelcomeImage from '@/shared/assets/images/demo-project-welcome.png';
import downloadVideoAnimationData from '@/shared/assets/lottie/download-video.json';
import audioTrackEditorAnimationData from '@/shared/assets/lottie/project-audio-track-editor.json';
import changeTextSectionAnimationData from '@/shared/assets/lottie/project-text-section.json';
import voiceCloneAnimationData from '@/shared/assets/lottie/project-voice-clone.json';
import { DOWNLOAD_OPTIONS_LINK, HOW_TO_EDIT_PROJECT_LINK, TEAMSPACES_DOC_LINK } from '@/shared/constants/docLinks';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { OnboardingStepKeys } from '@/shared/types/Onboarding';

import { OnboardingStep } from './types';

export const getOnboardingSteps = (): Record<OnboardingStepKeys, OnboardingStep> => {
  return {
    advancedTools_v1: {
      title: 'Advanced tools for power users',
      hasHighlight: false,
      imagePlacement: 'top',
      imageSrc: advancedToolsImage as string,
      minHeight: 118,
      placement: 'bottom',
      subtitle:
        'Access features like the Dictionary, Translation prompting, and SRT File Upload to enhance your translation workflow.',
      type: 'tooltip',
      width: 212,
    },
    audioTrackEditor_v1: {
      title: 'Adjust timestamps to synchronize the translated and original speech',
      actions: [
        {
          id: 1,
          title: 'Got it',
          type: 'button',
          handler: MixpanelEvents.onboardingStepAudioTrackEditorGotIt,
        },
      ],
      animationData: audioTrackEditorAnimationData,
      hasHighlight: true,
      type: 'tooltip',
      width: 280,
    },
    changeTextSection_v1: {
      title: 'Edit text, speakers and voice here',
      actions: [
        {
          id: 1,
          title: 'Learn more',
          type: 'link',
          handler: () => {
            MixpanelEvents.onboardingStepEditorLearnMore();
            window.open(HOW_TO_EDIT_PROJECT_LINK);
          },
        },
        {
          id: 2,
          title: 'Got it',
          type: 'button',
          handler: MixpanelEvents.onboardingStepEditorGotIt,
        },
      ],
      animationData: changeTextSectionAnimationData,
      hasHighlight: true,
      placement: 'right',
      type: 'tooltip',
      width: 254,
    },
    clickToPlay_v1: {
      title: 'Click play to see your new translated video',
      actions: [
        {
          id: 1,
          title: 'Okay',
          type: 'button',
          handler: MixpanelEvents.onboardingStepPlayerOkay,
        },
      ],
      hasHighlight: true,
      placement: 'left',
      type: 'tooltip',
      width: 210,
    },
    customizeVoiceClone_v1: {
      title: 'Customize your Voice Clone by adjusting expressiveness and similarity levels',
      actions: [
        {
          id: 1,
          title: 'Got it',
          type: 'button',
          handler: MixpanelEvents.onboardingStepVoiceGotIt,
        },
      ],
      animationData: voiceCloneAnimationData,
      hasHighlight: true,
      placement: 'bottom',
      type: 'tooltip',
      width: 281,
    },
    demoProjectWelcome_v1: {
      title: 'Welcome to the demo project',
      actions: [
        {
          id: 1,
          title: 'Get started',
          type: 'button',
        },
      ],
      hasHighlight: true,
      imageSrc: demoProjectWelcomeImage as string,
      subtitle:
        'Explore the original and translated versions of the video here. Experiment with the editor to get a feel for how it works!',
      type: 'modal',
    },
    discoverTeamspace_v1: {
      title: 'Discover Teamspaces',
      actions: [
        {
          id: 1,
          title: 'Learn more',
          type: 'link',
          handler: () => {
            MixpanelEvents.discoverTeamspaceLearnMore();
            window.open(TEAMSPACES_DOC_LINK);
          },
        },
        {
          id: 2,
          title: 'Got it',
          type: 'button',
        },
      ],
      hasHighlight: false,
      placement: 'right-end',
      subtitle:
        'Manage and collaborate with your team effortlessly by creating new Teamspaces and switching them here.',
      type: 'tooltip',
      width: 212,
    },
    downloadVideo_v1: {
      title: 'Download video here',
      actions: [
        {
          id: 1,
          title: 'Learn more',
          type: 'link',
          handler: () => {
            MixpanelEvents.onboardingStepDownloadLearnMore();
            window.open(DOWNLOAD_OPTIONS_LINK);
          },
        },
        {
          id: 2,
          title: 'Got it',
          type: 'button',
          handler: MixpanelEvents.onboardingStepDownloadGotIt,
        },
      ],
      animationData: downloadVideoAnimationData,
      hasHighlight: true,
      placement: 'top',
      type: 'tooltip',
      width: 202,
    },
    manageTeamspace_v1: {
      title: 'Manage your Teamspaces here',
      actions: [
        {
          id: 1,
          title: 'Got it',
          type: 'button',
        },
      ],
      hasHighlight: false,
      placement: 'right',
      subtitle: 'Easily switch between spaces right here to collaborate with your team.',
      type: 'tooltip',
      width: 212,
    },
    segmentPreview_v1: {
      title: 'Preview a segment before dubbing the whole video',
      actions: [
        {
          id: 1,
          title: 'Got it',
          type: 'button',
          handler: MixpanelEvents.onboardingStepSegmentPreviewGotIt,
        },
      ],
      hasHighlight: true,
      placement: 'bottom',
      subtitle: 'Use row playback to check timing and voice before applying changes to the full project.',
      type: 'tooltip',
      width: 260,
    },
    speakerVoicesButton_v1: {
      title: "Manage all speakers' voices and don't forget to apply changes",
      actions: [
        {
          id: 1,
          title: 'Got it',
          type: 'button',
          handler: MixpanelEvents.onboardingStepApplyChangesGotIt,
        },
      ],
      hasHighlight: true,
      placement: 'right',
      type: 'tooltip',
      width: 248,
    },
    transcriptVersions_v1: {
      title: 'Save, compare, and roll back versions',
      actions: [
        {
          id: 1,
          title: 'Got it',
          type: 'button',
          handler: MixpanelEvents.onboardingStepTranscriptVersionsGotIt,
        },
      ],
      hasHighlight: true,
      placement: 'bottom',
      subtitle:
        'Open version history to review changes, then use the rollback icon on an older version to restore it.',
      type: 'tooltip',
      width: 260,
    },
    translateMyOwnVideoButton_v1: {
      title: 'Translate a video of any length into 129 languages',
      actions: [
        {
          id: 1,
          title: 'Got it',
          type: 'button',
        },
      ],
      hasHighlight: true,
      imageSrc: allLanguagesImage as string,
      minHeight: 223,
      placement: 'bottom',
      type: 'tooltip',
      width: 281,
      onRender: MixpanelEvents.onboardingFinishedEditorDemo,
    },
    translationVariants_v1: {
      title: 'Try translation alternatives',
      actions: [
        {
          id: 1,
          title: 'Got it',
          type: 'button',
          handler: MixpanelEvents.onboardingStepTranslationVariantsGotIt,
        },
      ],
      hasHighlight: true,
      placement: 'top',
      subtitle: 'Pick a recommended or better-timed translation, then preview it in the row.',
      type: 'tooltip',
      width: 260,
    },
    videoPlayerTabs_v1: {
      title: 'Switch between original and translated videos',
      actions: [
        {
          id: 1,
          title: 'Got it',
          type: 'button',
        },
      ],
      hasHighlight: true,
      placement: 'left',
      type: 'tooltip',
      width: 246,
    },
  };
};

export const stepsWithNextStep = new Set<OnboardingStepKeys>([
  'advancedTools_v1',
  'audioTrackEditor_v1',
  'changeTextSection_v1',
  'clickToPlay_v1',
  'customizeVoiceClone_v1',
  'demoProjectWelcome_v1',
  'segmentPreview_v1',
  'speakerVoicesButton_v1',
  'transcriptVersions_v1',
  'translateMyOwnVideoButton_v1',
  'translationVariants_v1',
  'videoPlayerTabs_v1',
]);
